/**
 * Claim Cause Service
 * 
 * Service for fetching and managing claim causes from the backend.
 * Integrates with the backend's defaults API endpoint for claim cause resolution.
 * 
 * Evidence: Backend provides /api/v1/group/defaults/getClaimsCause endpoint
 * API Documentation: Returns claim causes with structure: [{"claim_cause_code": "1", "Description": "Natural Death", ...}]
 * Purpose: Provides claim causes for dropdown selection in claim forms
 */

import { apiClient } from '../utils/apiClient';

// Cache for claim cause data to avoid repeated API calls
let claimCauseCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds

/**
 * Fetch claim causes from the backend
 * @returns {Promise<Array>} Array of claim cause objects from backend
 * @throws {Error} If API call fails
 */
const fetchClaimCauses = async () => {
  try {
    console.log('🔍 ClaimCauseService: Starting fetchClaimCauses...');
    console.log('🌐 ClaimCauseService: Calling API endpoint: defaults/getClaimsCause');
    
    const response = await apiClient.get('defaults/getClaimsCause');
    
    console.log('📡 ClaimCauseService: Raw API response received:', {
      status: response.status,
      headers: response.headers,
      hasData: !!response.data,
      dataType: typeof response.data,
      dataConstructor: response.data?.constructor?.name,
      isArray: Array.isArray(response.data),
      dataKeys: response.data && typeof response.data === 'object' ? Object.keys(response.data) : 'N/A'
    });
    
    console.log('📋 ClaimCauseService: Full response.data structure:', response.data);
    console.log('🔍 ClaimCauseService: First item structure:', response.data[0] || 'No items');
    console.log('🔍 ClaimCauseService: First item keys:', response.data[0] ? Object.keys(response.data[0]) : 'No items');
    
    // Check if response.data is the array we need
    if (Array.isArray(response.data)) {
      console.log('✅ ClaimCauseService: response.data is array, length:', response.data.length);
      console.log('🎯 ClaimCauseService: First item structure:', response.data[0]);
      return response.data;
    }
    
    // Check if response.data has a nested data property
    if (response.data && Array.isArray(response.data.data)) {
      console.log('✅ ClaimCauseService: response.data.data is array, length:', response.data.data.length);
      console.log('🎯 ClaimCauseService: First item structure:', response.data.data[0]);
      console.log('🔍 ClaimCauseService: First nested item keys:', response.data.data[0] ? Object.keys(response.data.data[0]) : 'No items');
      return response.data.data;
    }
    
    // Fallback: return empty array if structure is unexpected
    console.warn('⚠️ ClaimCauseService: Unexpected response structure, returning empty array');
    return [];
    
  } catch (error) {
    console.error('❌ ClaimCauseService: Error in fetchClaimCauses:', {
      message: error.message,
      stack: error.stack,
      response: error.response?.data,
      status: error.response?.status
    });
    throw new Error(`Failed to fetch claim causes: ${error.message}`);
  }
};

/**
 * Get cached claim causes or fetch fresh data
 * @returns {Promise<Array>} Array of claim cause objects
 */
const getClaimCauses = async () => {
  const now = Date.now();
  
  // Return cached data if still valid
  if (claimCauseCache && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION)) {
    console.log('📋 ClaimCauseService: Returning cached claim causes, count:', claimCauseCache.length);
    return claimCauseCache;
  }
  
  // Fetch fresh data
  console.log('🔄 ClaimCauseService: Cache expired or empty, fetching fresh data...');
  const claimCauses = await fetchClaimCauses();
  
  // Update cache
  claimCauseCache = claimCauses;
  cacheTimestamp = now;
  
  console.log('💾 ClaimCauseService: Updated cache with claim causes, count:', claimCauses.length);
  return claimCauses;
};

/**
 * Format claim causes for dropdown display
 * @param {Array} claimCauses - Raw claim cause data from API
 * @returns {Array} Formatted options for dropdown
 */
const formatClaimCausesForDropdown = (claimCauses) => {
  if (!Array.isArray(claimCauses)) {
    console.warn('⚠️ ClaimCauseService: formatClaimCausesForDropdown received non-array:', claimCauses);
    return [];
  }
  
  const formatted = claimCauses.map((cause, index) => ({
    value: cause.description || `unknown-${index}`,
    label: cause.description || 'Unknown',
    code: cause.claimCauseCode || `unknown-${index}`,
    description: cause.description || 'Unknown',
    key: cause.id || `cause-${index}` // Add unique key for React
  }));
  
  console.log('🎨 ClaimCauseService: Formatted claim causes for dropdown:', {
    originalCount: claimCauses.length,
    formattedCount: formatted.length,
    sample: formatted[0]
  });
  
  return formatted;
};

/**
 * Find claim cause by code
 * @param {string} code - Claim cause code to find
 * @returns {Promise<Object|null>} Claim cause object or null if not found
 */
const findClaimCauseByCode = async (code) => {
  const claimCauses = await getClaimCauses();
  return claimCauses.find(cause => String(cause.claim_cause_code) === String(code)) || null;
};

/**
 * Validate if claim cause code exists
 * @param {string} code - Claim cause code to validate
 * @returns {Promise<boolean>} True if valid, false otherwise
 */
const isValidClaimCauseCode = async (code) => {
  const claimCause = await findClaimCauseByCode(code);
  return !!claimCause;
};

/**
 * Convert claim cause description back to code for backend submission
 * @param {string} description - Claim cause description (e.g., "HEART ATTACK")
 * @returns {Promise<string|null>} Claim cause code (e.g., "2") or null if not found
 */
const getCodeFromDescription = async (description) => {
  const claimCauses = await getClaimCauses();
  const cause = claimCauses.find(c => c.description === description);
  return cause ? cause.claimCauseCode : null;
};

/**
 * Clear cache (useful for testing or forced refresh)
 */
const clearCache = () => {
  console.log('🗑️ ClaimCauseService: Clearing cache...');
  claimCauseCache = null;
  cacheTimestamp = null;
};

// Export service methods
const claimCauseService = {
  fetchClaimCauses,
  getClaimCauses,
  formatClaimCausesForDropdown,
  findClaimCauseByCode,
  isValidClaimCauseCode,
  getCodeFromDescription,
  clearCache
};

export default claimCauseService;